import { useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type { CanvasNativeExportFormat, ProcessImageRequest } from '../types';
import { saveBlobLocally, type SaveBlobLocallyResult } from '../lib/save-blob-locally';

/** Human label for the FSA file-picker's format dropdown. */
const FORMAT_DESCRIPTIONS: Record<CanvasNativeExportFormat, string> = {
  png: 'PNG image',
  jpeg: 'JPEG image',
  webp: 'WebP image',
  avif: 'AVIF image',
  tiff: 'TIFF image',
};

interface UseImageExportOptions {
  exportToDataURL: (
    format: CanvasNativeExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => string;
  exportToBlob: (
    format: CanvasNativeExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => Promise<Blob | null>;
}

export function useImageExport({ exportToDataURL, exportToBlob }: UseImageExportOptions) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /** Save the canvas locally. Preferred path: File System Access API
   *  (returns a `'saved-with-handle'` result so callers can pin the
   *  handle on the active tab and let a follow-up Save write
   *  in-place). Falls back to iOS Web Share or `<a download>` —
   *  those return `'saved'` with no handle. A user-cancelled picker
   *  returns `'cancelled'` so callers can suppress success toasts. */
  const exportToLocal = useCallback(
    async (
      fileName: string,
      format: CanvasNativeExportFormat = 'png',
      quality = 1,
      target?: { width?: number; height?: number },
    ): Promise<SaveBlobLocallyResult> => {
      setIsExporting(true);
      setExportError(null);

      try {
        const blob = await exportToBlob(format, quality, target);
        if (!blob) throw new Error('Failed to export canvas');

        return await saveBlobLocally(blob, {
          fileName,
          extension: format,
          description: FORMAT_DESCRIPTIONS[format],
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed';
        setExportError(message);
        // Surface the error as a toast directly. Callers can also
        // read `exportError` from the hook, but no current consumer
        // does — without this toast, an encode failure leaves the
        // user staring at an unresponsive Save button with no UI
        // feedback. Returning `'cancelled'` so post-save side
        // effects (markClean, history snapshot) are skipped.
        toast({
          title: 'Export failed',
          description: message,
          variant: 'destructive',
        });
        return { status: 'cancelled' };
      } finally {
        setIsExporting(false);
      }
    },
    [exportToBlob],
  );

  /** Send canvas data to server for Sharp processing and save to Fias */
  const exportToFias = useCallback(async (request: ProcessImageRequest) => {
    setIsExporting(true);
    setExportError(null);

    try {
      const result = await api.processImage(request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Server export failed';
      setExportError(message);
      return null;
    } finally {
      setIsExporting(false);
    }
  }, []);

  /** Quick save canvas as PNG to Fias filesystem */
  const quickSave = useCallback(
    async (fileName: string) => {
      const dataUrl = exportToDataURL('png', 1);
      if (!dataUrl) return null;

      return exportToFias({
        sourceDataUrl: dataUrl,
        operations: [],
        output: { format: 'png' },
        saveAs: { fileName },
      });
    },
    [exportToDataURL, exportToFias],
  );

  return {
    isExporting,
    exportError,
    exportToLocal,
    exportToFias,
    quickSave,
  };
}
